import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';

import { apiServices } from 'apps/marsinet/src/app/services/apiServices';
import {
  AllocatedTimeDTO,
  IncidenceTableDto,
  MaintenanceOrderDetailsDto,
  MaterialTableDto,
} from 'apps/marsinet/src/app/typescript-axios';

import AllocateTimeModal from './modal-allocate-time-to-om';
import {
  AllocatedTimesList,
  AssociatedIncidencesList,
  MaintenanceOrderDetailsCard,
  MaterialList,
} from './details-maintenance-order-components';
import AssignMaterialModal from './modal-allocate-material-to-om';
import {
  CloseMaintenanceOrderData,
  CloseMaintenanceOrderDialog,
} from './closeMaintenanceOrderDialog';
import AssignIncidentToOmModal from '../assign-incidence-to-om/assign-incidence-to-om-modal';
import { ChangeMaintenanceOrderStatusDialog } from './changeMaintenanceOrderStatusDialog';
import { MAINTENANCE_TYPE_IDS } from './create-maintenance-order-modal';
import { MarsinetContext } from 'apps/marsinet/src/app/context/marsinetProvider';
import { localDateTimeToUtc } from 'apps/marsinet/src/app/utils/timezone-utils';
import ModalSuggestDeviceState from 'apps/marsinet/src/app/components/modal-suggest-device-state/modal-suggest-device-state';
import { useSuggestDeviceState } from 'apps/marsinet/src/app/components/modal-suggest-device-state/use-suggest-device-state';

type DetailsLocationState = {
  maintenance_order?: MaintenanceOrderDetailsDto;
} | null;

/** Pulls the API error message when there is one, otherwise falls back. */
const getErrorMessage = (error: unknown, fallback: string): string => {
  const message = (error as { response?: { data?: { message?: string } } })
    ?.response?.data?.message;
  return message ?? fallback;
};

type SectionHeaderProps = {
  icon: string;
  label: string;
};

const SectionHeader = ({ icon, label }: SectionHeaderProps) => (
  <div className="col-12">
    <div className="flex w-full relative align-items-center justify-content-center my-3 px-4">
      <div className="border-top-1 surface-border top-50 left-0 absolute w-full"></div>
      <div className="px-2 z-1 surface-50 flex align-items-center gap-2">
        <i className={`${icon} text-lg text-900`} />
        <span className="text-lg font-semibold text-900">{label}</span>
      </div>
    </div>
  </div>
);

export function DetailsMaintenanceOrder() {
  const { t } = useTranslation();
  const { selectedTimezone } = useContext(MarsinetContext);
  const { id_om } = useParams<{ id_om: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { suggestForSerial: suggestAfterClose, modalProps } = useSuggestDeviceState(['operations', 'on_client']);

  const cachedOrder = (location.state as DetailsLocationState)
    ?.maintenance_order;

  const [maintenanceOrder, setMaintenanceOrder] =
    useState<MaintenanceOrderDetailsDto | null>(cachedOrder ?? null);
  const [loading, setLoading] = useState(!cachedOrder);

  const [associatedIncidences, setAssociatedIncidences] = useState<
    IncidenceTableDto[]
  >([]);
  const [loadingIncidences, setLoadingIncidences] = useState(true);

  const [allocatedTimes, setAllocatedTimes] = useState<AllocatedTimeDTO[]>([]);
  const [loadingAllocatedTimes, setLoadingAllocatedTimes] = useState(true);
  const [showAllocateModal, setShowAllocateModal] = useState(false);

  const [removingMaterialId, setRemovingMaterialId] = useState<string | null>(
    null
  );
  const [showAssignMaterialModal, setShowAssignMaterialModal] = useState(false);

  const [assignedMaterials, setAssignedMaterials] = useState<
    MaterialTableDto[]
  >([]);
  const [loadingAssignedMaterials, setLoadingAssignedMaterials] =
    useState(true);

  const [assignIncidentModalVisible, setAssignIncidentModalVisible] =
    useState(false);
  const [statusDialogVisible, setStatusDialogVisible] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [closeDialogVisible, setCloseDialogVisible] = useState(false);
  const [closingOrder, setClosingOrder] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const toast = useRef<Toast>(null);
  const pendingSuggestionSerial = useRef<number | null>(null);

  const isClosed = maintenanceOrder?.status == 'Terminado';

  const isCorrective =
    maintenanceOrder?.maintenance_type_id === MAINTENANCE_TYPE_IDS.CORRECTIVE;
  // El cierre sólo se ofrece desde aquí, y sólo si la orden sigue abierta.
  const canCloseOrder = Boolean(maintenanceOrder) && !isClosed;

  /* ---------------------------------------------------------------- load */

  const loadMaintenanceOrderDetail = useCallback(async () => {
    if (!id_om) {
      setLoading(false);
      return;
    }

    try {
      const response =
        await apiServices.maintenanceOrderApi.maintenanceOrdersControllerGetMaintenanceOrderDetailsById(
          id_om
        );
      setMaintenanceOrder(response.data);
    } catch (error) {
      console.error('[DetailsMaintenanceOrder] detail', error);
      toast.current?.show({
        severity: 'error',
        summary: t('messages.error'),
        detail: t('pages.maintenance_orders.can_not_load'),
      });
    } finally {
      setLoading(false);
    }
  }, [id_om, t, toast]);

  const loadAssociatedIncidences = useCallback(async () => {
    if (!id_om) {
      setLoadingIncidences(false);
      return;
    }

    try {
      setLoadingIncidences(true);
      const response =
        await apiServices.incidenceApi.incidenceControllerGetIncidencesByMaintenanceOrder(
          id_om
        );
      setAssociatedIncidences(response.data ?? []);
    } catch (error) {
      console.error('[DetailsMaintenanceOrder] incidences', error);
      toast.current?.show({
        severity: 'error',
        summary: t('messages.error'),
        detail: t('pages.maintenance_orders.can_not_load_incidences'),
      });
      setAssociatedIncidences([]);
    } finally {
      setLoadingIncidences(false);
    }
  }, [id_om, t, toast]);

  const loadAllocatedTimes = useCallback(async () => {
    if (!id_om) {
      setLoadingAllocatedTimes(false);
      return;
    }

    try {
      setLoadingAllocatedTimes(true);
      const response =
        await apiServices.maintenanceOrderApi.maintenanceOrdersControllerGetAllocatedTimesByMaintenanceOrderId(
          id_om
        );
      setAllocatedTimes(response.data ?? []);
    } catch (error) {
      console.error('[DetailsMaintenanceOrder] allocated times', error);
      toast.current?.show({
        severity: 'error',
        summary: t('messages.error'),
        detail: t('pages.maintenance_orders.can_not_load_allocated_times'),
      });
      setAllocatedTimes([]);
    } finally {
      setLoadingAllocatedTimes(false);
    }
  }, [id_om, t, toast]);

  const reloadAssignedMaterials = useCallback(async () => {
    if (!id_om) {
      setLoadingAssignedMaterials(false);
      return;
    }

    try {
      setLoadingAssignedMaterials(true);

      const response =
        await apiServices.maintenanceOrderApi.maintenanceOrdersControllerGetMaterialsByMaintenanceOrderId(
          id_om
        );

      setAssignedMaterials((response.data ?? []) as MaterialTableDto[]);
    } catch (error) {
      console.error('[DetailsMaintenanceOrder] materials', error);
      setAssignedMaterials([]);
    } finally {
      setLoadingAssignedMaterials(false);
    }
  }, [id_om]);

  useEffect(() => {
    loadMaintenanceOrderDetail();
  }, [loadMaintenanceOrderDetail]);

  useEffect(() => {
    loadAssociatedIncidences();
  }, [loadAssociatedIncidences]);

  useEffect(() => {
    loadAllocatedTimes();
  }, [loadAllocatedTimes]);

  useEffect(() => {
    reloadAssignedMaterials();
  }, [reloadAssignedMaterials]);

  /* -------------------------------------------------------------- actions */

  const handleRemoveMaterial = useCallback(
    (materialId: string, maintenanceOrderId: string) => {
      confirmDialog({
        message: t('pages.maintenance_orders.confirm_remove_material_message'),
        header: t('pages.maintenance_orders.confirm_remove_material_title'),
        icon: 'pi pi-exclamation-triangle',
        accept: async () => {
          try {
            setRemovingMaterialId(materialId);

            await apiServices.maintenanceOrderApi.maintenanceOrdersControllerDeleteMaterialFromMaintenanceOrder(
              materialId
            );

            toast.current?.show({
              severity: 'success',
              summary: t('messages.success'),
              detail: t('pages.maintenance_orders.material_removed_success'),
            });

            await reloadAssignedMaterials();
          } catch (error) {
            console.error('[DetailsMaintenanceOrder] remove material', error);
            toast.current?.show({
              severity: 'error',
              summary: t('messages.error'),
              detail: getErrorMessage(
                error,
                t('pages.maintenance_orders.error_removing_material')
              ),
            });
          } finally {
            setRemovingMaterialId(null);
          }
        },
      });
    },
    [reloadAssignedMaterials, t, toast]
  );

  const handleDownloadPdf = useCallback(
    async (maintenanceOrderId: string) => {
      if (!maintenanceOrderId) return;

      setDownloadingPdf(true);
      let url: string | undefined;

      try {
        const response =
          await apiServices.maintenanceOrderApi.maintenanceOrdersControllerDownloadMaintenanceOrdersPdfById(
            maintenanceOrderId,
            { responseType: 'blob' }
          );

        const blob = new Blob([response.data], { type: 'application/pdf' });
        url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `OM-${maintenanceOrder?.code ?? maintenanceOrderId}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        console.error('[DetailsMaintenanceOrder] download pdf', error);
        toast.current?.show({
          severity: 'error',
          summary: t('messages.error'),
          detail: t('pages.maintenance_orders.pdf_download_error'),
        });
      } finally {
        if (url) window.URL.revokeObjectURL(url);
        setDownloadingPdf(false);
      }
    },
    [maintenanceOrder?.code, t]
  );

  const handleChangeStatus = useCallback(
    async (statusId: string): Promise<boolean> => {
      if (!maintenanceOrder?.id) return false;

      try {
        setChangingStatus(true);

        await apiServices.maintenanceOrderApi.maintenanceOrdersControllerChangeMaintenanceOrderStatus(
          { status: statusId },
          maintenanceOrder.id
        );

        await loadMaintenanceOrderDetail();
        setStatusDialogVisible(false);

        toast.current?.show({
          severity: 'success',
          summary: t('messages.success'),
          detail: t('pages.maintenance_orders.changeStatus.updated'),
        });

        return true;
      } catch (error) {
        console.error('[DetailsMaintenanceOrder] change status', error);

        toast.current?.show({
          severity: 'error',
          summary: t('messages.error'),
          detail: getErrorMessage(
            error,
            t('pages.maintenance_orders.changeStatus.error')
          ),
        });

        return false;
      } finally {
        setChangingStatus(false);
      }
    },
    [loadMaintenanceOrderDetail, maintenanceOrder?.id, t]
  );

  /**
   * Cierra primero la OM y después las incidencias marcadas como resueltas.
   *
   * El orden importa: el backend rechaza (409) el cierre de una incidencia que
   * todavía tenga órdenes de mantenimiento sin finalizar, así que cerrar la
   * incidencia antes que la OM fallaría siempre.
   */
  const handleConfirmClose = useCallback(
    async (closeData: CloseMaintenanceOrderData): Promise<boolean> => {
      if (!maintenanceOrder?.id) return false;

      const resolvedIncidenceIds = Array.from(
        new Set(closeData.resolvedIncidenceIds ?? [])
      );
      const comment = closeData.comment?.trim() ?? '';

      setClosingOrder(true);

      try {
        const closingDateTime = closeData.selectedDateTime ?? new Date();
        // La orden y sus incidencias resueltas reciben el mismo instante UTC.
        const closingIso = localDateTimeToUtc(
          closingDateTime,
          selectedTimezone
        );

        // 1. La orden de mantenimiento.
        await apiServices.maintenanceOrderApi.maintenanceOrdersControllerCloseMaintenanceOrder(
          { closing_datetime: closingIso },
          maintenanceOrder.id
        );

        // 2. Las incidencias resueltas, ya sin la OM bloqueándolas.
        const results = await Promise.allSettled(
          resolvedIncidenceIds.map((incidenceId) =>
            apiServices.incidenceApi.incidenceControllerCloseIncidence(
              {
                closing_datetime: closingIso,
                comment,
                maintenance_order_id: maintenanceOrder.id,
              },
              incidenceId
            )
          )
        );

        const failures = results.filter(
          (result): result is PromiseRejectedResult =>
            result.status === 'rejected'
        );

        await Promise.all([
          loadMaintenanceOrderDetail(),
          loadAssociatedIncidences(),
        ]);

        pendingSuggestionSerial.current =
          maintenanceOrder.associated_component?.serial ?? null;

        // La OM ya está cerrada aunque alguna incidencia falle: no se puede
        // deshacer, así que se avisa y se deja continuar.
        if (failures.length > 0) {
          console.error(
            '[DetailsMaintenanceOrder] close incidences',
            failures.map((failure) => failure.reason)
          );

          toast.current?.show({
            severity: 'warn',
            summary: t('messages.warning'),
            detail: getErrorMessage(
              failures[0].reason,
              t('pages.maintenance_orders.closeDialog.incidences_close_error', {
                count: failures.length,
              })
            ),
            life: 8000,
          });

          return true;
        }

        toast.current?.show({
          severity: 'success',
          summary: t('messages.success'),
          detail: t('pages.maintenance_orders.closeDialog.closed'),
        });

        return true;
      } catch (error) {
        console.error('[DetailsMaintenanceOrder] close order', error);
        toast.current?.show({
          severity: 'error',
          summary: t('messages.error'),
          detail: getErrorMessage(
            error,
            t('pages.maintenance_orders.closeDialog.close_error')
          ),
        });
        return false;
      } finally {
        setClosingOrder(false);
      }
    },
    [
      loadAssociatedIncidences,
      loadMaintenanceOrderDetail,
      maintenanceOrder?.id,
      selectedTimezone,
      t,
    ]
  );

  /* --------------------------------------------------------------- render */

  return (
    <div className="col-12 xl:pl-7 xl:pr-7">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="col-12">
        <div className="surface-card p-3 shadow-3 border-round-xl flex justify-content-between align-items-center mb-5">
          <div className="p-3 flex align-items-center flex-wrap">
            <Button
              icon="pi pi-angle-left"
              rounded
              severity="secondary"
              className="mr-2 mb-2 sm:mr-4"
              aria-label={t('goBack')}
              onClick={() => navigate('/authenticated/incidents')}
            />
            <div className="mr-3">
              <i className="pi pi-wrench text-2xl md:text-3xl text-900"></i>
            </div>
            <div className="font-medium text-lg sm:text-xl md:text-2xl text-700">
              {t('pages.maintenance_orders.detailsOm')}{' '}
              {maintenanceOrder?.code ?? ''}
            </div>
          </div>

          <div className="p-3 flex align-items-center flex-wrap gap-2">
            {!isClosed && (
              <Button
                label={t('pages.maintenance_orders.changeStatus.button')}
                icon="pi pi-sync"
                severity="secondary"
                outlined
                onClick={() => setStatusDialogVisible(true)}
              />
            )}

            {canCloseOrder && (
              <Button
                label={t('pages.incidents.buttonCloseOM')}
                icon="pi pi-check-circle"
                severity="danger"
                onClick={() => setCloseDialogVisible(true)}
              />
            )}
          </div>
        </div>
      </div>

      <div className="col-12 grid mx-0">
        <div className="surface-card border-round-xl shadow-3 px-3 col-12 mb-5">
          {loading ? (
            <div className="p-6 flex justify-content-center">
              <ProgressSpinner
                style={{ width: '3rem', height: '3rem' }}
                strokeWidth="4"
              />
            </div>
          ) : !maintenanceOrder ? (
            <div className="p-4 text-center">
              <p className="text-700 mb-3">
                {t('pages.maintenance_orders.can_not_load')}
              </p>
              <Button
                label={t('goBack')}
                icon="pi pi-arrow-left"
                onClick={() => navigate('/authenticated/incidents')}
              />
            </div>
          ) : (
            <>
              <SectionHeader
                icon="pi pi-info-circle"
                label={t('pages.maintenance_orders.details')}
              />

              <MaintenanceOrderDetailsCard
                maintenanceOrder={maintenanceOrder}
              />

              {/* Associated incidences */}
              <SectionHeader
                icon="pi pi-exclamation-triangle"
                label={t('pages.maintenance_orders.associated_incidences')}
              />

              {!isClosed && (
                <div className="col-12 flex justify-content-end mb-3 px-4">
                  <Button
                    label={t('pages.incidents.buttonAssing')}
                    icon="pi pi-link"
                    size="small"
                    onClick={() => setAssignIncidentModalVisible(true)}
                  />
                </div>
              )}

              <AssociatedIncidencesList
                incidences={associatedIncidences}
                loading={loadingIncidences}
                onOpenIncidence={(incidence) =>
                  navigate(`/authenticated/incidents/${incidence.id}`, {
                    state: { incidence },
                  })
                }
              />

              {/* Allocated times */}
              <SectionHeader
                icon="pi pi-clock"
                label={t('pages.maintenance_orders.allocated_times')}
              />

              {!isClosed && (
                <div className="col-12 flex justify-content-end mb-3 px-4">
                  <Button
                    label={t('pages.maintenance_orders.allocate_time')}
                    icon="pi pi-plus"
                    size="small"
                    onClick={() => setShowAllocateModal(true)}
                  />
                </div>
              )}

              <AllocatedTimesList
                allocatedTimes={allocatedTimes}
                loading={loadingAllocatedTimes}
              />

              {/* Assigned materials */}
              <SectionHeader
                icon="pi pi-check-circle"
                label={t('pages.maintenance_orders.assigned_materials')}
              />

              {!isClosed && (
                <div className="col-12 flex justify-content-end mb-3 px-4">
                  <Button
                    label={t('pages.maintenance_orders.assign_material')}
                    icon="pi pi-plus"
                    size="small"
                    onClick={() => setShowAssignMaterialModal(true)}
                  />
                </div>
              )}

              <MaterialList
                loadingAssignedMaterials={loadingAssignedMaterials}
                assignedMaterials={assignedMaterials}
                maintenanceOrderId={maintenanceOrder.id}
                onRemoveMaterial={handleRemoveMaterial}
                removingMaterialId={removingMaterialId}
                readOnly={isClosed}
              />

              <div className="col-12 mb-4" />

              <AllocateTimeModal
                visible={showAllocateModal}
                maintenanceOrderId={maintenanceOrder.id}
                onHide={() => setShowAllocateModal(false)}
                onSuccess={loadAllocatedTimes}
              />

              <AssignIncidentToOmModal
                visible={assignIncidentModalVisible}
                setVisible={setAssignIncidentModalVisible}
                maintenanceOrderId={maintenanceOrder.id}
                deviceSerial={maintenanceOrder.associated_component?.serial}
                onAssignedSuccess={loadAssociatedIncidences}
              />

              <ChangeMaintenanceOrderStatusDialog
                visible={statusDialogVisible}
                loading={changingStatus}
                currentStatus={maintenanceOrder.status}
                maintenanceOrderCode={
                  maintenanceOrder.code ?? maintenanceOrder.id
                }
                onCancel={() => {
                  if (changingStatus) return;
                  setStatusDialogVisible(false);
                }}
                onConfirm={handleChangeStatus}
              />

              <CloseMaintenanceOrderDialog
                visible={closeDialogVisible}
                loading={closingOrder}
                downloadingPdf={downloadingPdf}
                loadingIncidents={loadingIncidences}
                maintenanceOrderId={maintenanceOrder.id}
                maintenanceOrderCode={
                  maintenanceOrder.code ?? maintenanceOrder.id
                }
                maintenanceOrderStatus={maintenanceOrder.status ?? ''}
                associatedIncidents={associatedIncidences}
                materialImputationsCount={assignedMaterials.length}
                timeImputationsCount={allocatedTimes.length}
                allowManualClosingDate={false}
                onCancel={() => {
                  if (closingOrder || downloadingPdf) return;
                  setCloseDialogVisible(false);

                  if (pendingSuggestionSerial.current !== null) {
                    suggestAfterClose(pendingSuggestionSerial.current);
                    pendingSuggestionSerial.current = null;
                  }
                }}
                onConfirm={handleConfirmClose}
                onDownloadPdf={handleDownloadPdf}
                isCorrective={isCorrective}
              />

              <ModalSuggestDeviceState {...modalProps} />

              <AssignMaterialModal
                visible={showAssignMaterialModal}
                maintenanceOrderId={maintenanceOrder.id}
                onHide={() => setShowAssignMaterialModal(false)}
                onSuccess={(material) => {
                  setAssignedMaterials((prev) => [...prev, material]);
                  toast.current?.show({
                    severity: 'success',
                    summary: t('messages.success'),
                    detail: t(
                      'pages.maintenance_orders.material_assigned_success'
                    ),
                  });
                }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default DetailsMaintenanceOrder;
